<template>
  <BModal
    :model-value="modal"
    scrollable
    centered
    size="lg"
    no-close-on-backdrop
    title="Extra Charge"
    @update:modelValue="updateModal"
  >
    <template #header>
      <div class="d-flex align-items-center justify-content-between w-100">
        <h5 class="mb-0 font-weight-bold">Extra Charge</h5>
      </div>
      <BButton variant="link" class="close text-dark" @click="closeModal">
        <i class="ion ion-android-close"></i>
      </BButton>
    </template>

    <div v-if="isLoading" class="text-center py-4 text-muted small">
      <i class="fas fa-spinner fa-spin mr-1"></i> Loading...
    </div>

    <BForm v-else>
      <!-- Item Rows -->
      <BRow
        v-for="(ext, index) in extraChargeItems"
        :key="index"
        class="mb-2 align-items-center"
      >
        <BCol md="4">
          <label v-if="index === 0" class="small text-muted mb-1">Category</label>
          <div class="d-flex align-items-center">
            <span
              v-if="isFromInhouse && !ext.is_late_added && ext.selectedExtraChargeUuid"
              class="badge badge-success mr-2"
              title="Paid on Reservation"
            >
              <i class="fas fa-lock mr-1"></i>PAID
            </span>
            <BFormSelect
              v-model="ext.selectedExtraChargeCategory"
              :options="extraChargeCategories"
              text-field="name"
              value-field="uuid"
              size="sm"
              :disabled="isFromInhouse && !ext.is_late_added && ext.selectedExtraChargeUuid"
              @change="handleExtraChargeCategory(ext.selectedExtraChargeCategory, index)"
            >
              <template #first>
                <BFormSelectOption :value="null" disabled>Select Category</BFormSelectOption>
              </template>
            </BFormSelect>
          </div>
        </BCol>

        <BCol md="3">
          <label v-if="index === 0" class="small text-muted mb-1">Item</label>
          <BFormSelect
            v-model="ext.selectedExtraChargeUuid"
            :options="ext.extracharges"
            text-field="name"
            value-field="uuid"
            size="sm"
            :disabled="isFromInhouse && !ext.is_late_added && ext.selectedExtraChargeUuid"
            @change="handleExtraCharge(ext.selectedExtraChargeUuid, index)"
          >
            <template #first>
              <BFormSelectOption :value="null" disabled>Select Item</BFormSelectOption>
            </template>
          </BFormSelect>
        </BCol>

        <BCol md="2">
          <label v-if="index === 0" class="small text-muted mb-1">Qty</label>
          <BInputGroup size="sm">
            <BButton
              variant="light"
              class="border px-2"
              :disabled="isFromInhouse && !ext.is_late_added && ext.selectedExtraChargeUuid"
              @click="decreaseQuantity(index)"
            >
              <i class="fas fa-minus" style="font-size: 10px;"></i>
            </BButton>
            <BFormInput
              type="number"
              v-model.number="ext.quantity"
              min="0"
              class="text-center px-1"
              :disabled="isFromInhouse && !ext.is_late_added && ext.selectedExtraChargeUuid"
            />
            <BButton
              variant="light"
              class="border px-2"
              :disabled="isFromInhouse && !ext.is_late_added && ext.selectedExtraChargeUuid"
              @click="increaseQuantity(index)"
            >
              <i class="fas fa-plus" style="font-size: 10px;"></i>
            </BButton>
          </BInputGroup>
        </BCol>

        <BCol md="2">
          <label v-if="index === 0" class="small text-muted mb-1">Price (IDR)</label>
          <BFormInput
            type="number"
            v-model.number="ext.price"
            min="0"
            size="sm"
            placeholder="0"
            :disabled="isFromInhouse && !ext.is_late_added && ext.selectedExtraChargeUuid"
          />
        </BCol>

        <BCol md="1" class="text-center">
          <label v-if="index === 0" class="small text-muted mb-1 d-block">&nbsp;</label>
          <button
            v-if="!(isFromInhouse && !ext.is_late_added && ext.selectedExtraChargeUuid)"
            type="button"
            class="btn btn-sm btn-link text-danger p-0"
            @click="removeRow(index)"
            title="Remove item"
          >
            <i class="fas fa-trash-alt"></i>
          </button>
          <span
            v-else
            class="text-muted small"
            title="Reservation items cannot be deleted in In-House"
          >
            <i class="fas fa-lock"></i>
          </span>
        </BCol>
      </BRow>

      <!-- Action buttons & Total in 1 line -->
      <div class="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
        <BButton variant="outline-primary" size="sm" @click="addItemExtraCharge">
          <i class="fas fa-plus mr-1"></i> Add Item
        </BButton>
        <p class="m-0">
          Total Extra Charge : <strong>{{ formatIDR(totalExtraChargePrice) }}</strong>
        </p>
      </div>
    </BForm>

    <template #footer>
      <div class="d-flex justify-content-end">
        <BButton
          variant="primary"
          @click="handleAction"
          class="mr-2"
          :disabled="isSubmitting || isLoading"
        >
          {{ isSubmitting ? "Saving..." : "Save" }}
        </BButton>
        <BButton variant="danger" @click="closeModal">Cancel</BButton>
      </div>
    </template>
  </BModal>

  <ToastResponse
    v-model="modalResponse.toast"
    :text="modalResponse.text"
    :success="modalResponse.success"
  />
</template>

<script>
import { ref, computed, inject, onMounted, watch } from "vue";
import { formatIDR } from "@/utils/currency";
import ToastResponse from "@/components/ToastResponse.vue";
import {
  BModal,
  BFormInput,
  BFormSelect,
  BFormSelectOption,
  BForm,
  BRow,
  BCol,
  BInputGroup,
  BButton,
} from "bootstrap-vue-next";

export default {
  components: {
    ToastResponse,
    BModal,
    BFormInput,
    BFormSelect,
    BFormSelectOption,
    BForm,
    BRow,
    BCol,
    BInputGroup,
    BButton,
  },
  props: {
    modal: Boolean,
    uuid: String,
    from: String,
  },
  emits: ["update:modal", "addedExtraCharge"],
  setup(props, { emit }) {
    const repositories = inject("repositories");
    const extraChargeCategoryRepository = repositories?.extraChargeCategory;
    const extraChargeRepository = repositories?.extraCharges;
    const reservationRepository = repositories?.reservations;

    const isLateAdded = ref(false);
    const isLoading = ref(false);
    const isSubmitting = ref(false);

    const isFromInhouse = computed(() => props.from === "inhouse");

    const extraChargeItems = ref([
      {
        selectedExtraChargeUuid: null,
        selectedExtraChargeCategory: null,
        extracharges: [],
        quantity: 1,
        price: 0,
        is_late_added: true,
      },
    ]);
    const extraChargeCategories = ref([]);

    const modalResponse = ref({ toast: false, text: "", success: false });

    // Fetch master categories
    const getListExtraChargeCategory = async () => {
      try {
        const res = await extraChargeCategoryRepository.getAll();
        extraChargeCategories.value = res.data.data;
      } catch (err) {
        console.error(err);
      }
    };

    // Fetch items for specific category
    const getListExtraCharge = async (categoryUuid, index) => {
      try {
        const res = await extraChargeRepository.getAll({
          extra_charge_category_uuid: categoryUuid,
        });
        if (extraChargeItems.value[index]) {
          extraChargeItems.value[index].extracharges = res.data.data;
        }
      } catch (err) {
        console.error(err);
      }
    };

    // Load existing items into the single form
    const loadReservationExtraCharges = async () => {
      if (!props.uuid) return;
      isLoading.value = true;
      try {
        // Pastikan categories sudah ada
        if (extraChargeCategories.value.length === 0) {
          await getListExtraChargeCategory();
        }

        const res = await reservationRepository.getById(props.uuid);
        const data = res?.data?.data || res?.data;
        const currentCharges = (data?.extracharge || []).filter(
          (e) => Number(e.quantity) > 0
        );

        if (currentCharges.length > 0) {
          extraChargeItems.value = currentCharges.map((item) => {
            const catUuid =
              item.extra_charge?.extracharge_category?.uuid ||
              item.extra_charge?.extra_charge_category_uuid ||
              item.extra_charge?.category?.uuid ||
              null;

            return {
              selectedExtraChargeUuid: item.extra_charge_uuid,
              selectedExtraChargeCategory: catUuid,
              extracharges: item.extra_charge ? [item.extra_charge] : [],
              quantity: item.quantity,
              price: Number(item.price) || 0,
              is_late_added: Boolean(item.is_late_added),
            };
          });

          // Fetch full category item list for each pre-filled row
          currentCharges.forEach((item, idx) => {
            const catUuid =
              item.extra_charge?.extracharge_category?.uuid ||
              item.extra_charge?.extra_charge_category_uuid ||
              item.extra_charge?.category?.uuid;
            if (catUuid) {
              getListExtraCharge(catUuid, idx);
            }
          });
        } else {
          resetForm();
        }
      } catch (err) {
        console.error("Failed to load extra charges:", err);
      } finally {
        isLoading.value = false;
      }
    };

    const addItemExtraCharge = () => {
      extraChargeItems.value.push({
        selectedExtraChargeUuid: null,
        selectedExtraChargeCategory: null,
        extracharges: [],
        quantity: 1,
        price: 0,
        is_late_added: isFromInhouse.value ? true : false,
      });
    };

    const removeRow = (index) => {
      if (extraChargeItems.value.length > 1) {
        extraChargeItems.value.splice(index, 1);
      } else {
        extraChargeItems.value = [
          {
            selectedExtraChargeUuid: null,
            selectedExtraChargeCategory: null,
            extracharges: [],
            quantity: 1,
            price: 0,
          },
        ];
      }
    };

    const increaseQuantity = (index) =>
      extraChargeItems.value[index].quantity++;
    const decreaseQuantity = (index) =>
      extraChargeItems.value[index].quantity > 0 &&
      extraChargeItems.value[index].quantity--;

    const closeModal = () => {
      resetForm();
      updateModal(false);
    };

    const updateModal = (value) => emit("update:modal", value);

    const totalExtraChargePrice = computed(() => {
      return extraChargeItems.value.reduce((sum, ext) => {
        const price = Number(ext.price) || 0;
        const qty = Number(ext.quantity) || 0;
        return sum + price * qty;
      }, 0);
    });

    const handleExtraChargeCategory = (category, index) => {
      extraChargeItems.value[index].selectedExtraChargeUuid = null;
      extraChargeItems.value[index].price = 0;
      getListExtraCharge(category, index);
    };

    const handleExtraCharge = (uuid, index) => {
      extraChargeItems.value[index].selectedExtraChargeUuid = uuid;
      const selected = extraChargeItems.value[index].extracharges.find(
        (item) => item.uuid === uuid
      );
      if (selected) {
        extraChargeItems.value[index].price = Number(selected.price) || 0;
      }
    };

    const handleAction = () => {
      if (props.uuid) {
        saveExtraCharges();
      }
    };

    const saveExtraCharges = async () => {
      isSubmitting.value = true;
      const validItems = extraChargeItems.value.filter(
        (item) => item.selectedExtraChargeUuid !== null && item.quantity > 0
      );

      const payload = {
        from: props.from || (isFromInhouse.value ? "inhouse" : "reservation"),
        extra_charges: validItems.map((item) => {
          return {
            uuid: item.selectedExtraChargeUuid,
            quantity: item.quantity,
            price: Number(item.price) || 0,
            is_late_added: item.is_late_added !== undefined ? item.is_late_added : isLateAdded.value,
          };
        }),
      };

      try {
        const response = await reservationRepository.addExtraCharge(
          props.uuid,
          payload
        );
        modalResponse.value = {
          toast: true,
          text: response.data.message || "Extra charge saved successfully.",
          success: true,
        };
        emit("addedExtraCharge");
        updateModal(false);
        resetForm();
      } catch (error) {
        modalResponse.value = {
          toast: true,
          text: error.response?.data?.message || "Failed to save extra charge.",
          success: false,
        };
      } finally {
        isSubmitting.value = false;
      }
    };

    const resetForm = () => {
      extraChargeItems.value = [
        {
          selectedExtraChargeUuid: null,
          selectedExtraChargeCategory: null,
          extracharges: [],
          quantity: 1,
          price: 0,
        },
      ];
    };

    watch(
      () => props.modal,
      (value) => {
        if (value) {
          getListExtraChargeCategory();
          loadReservationExtraCharges();
        }
      }
    );

    watch(
      () => props.from,
      (value) => {
        if (value === "reservation" || value === "checkin")
          isLateAdded.value = false;
        if (value === "inhouse") isLateAdded.value = true;
      }
    );

    onMounted(() => {
      if (props.from === "reservation" || props.from === "checkin")
        isLateAdded.value = false;
      if (props.from === "inhouse") isLateAdded.value = true;
    });

    return {
      isLoading,
      isSubmitting,
      isLateAdded,
      isFromInhouse,
      extraChargeItems,
      extraChargeCategories,
      modalResponse,
      saveExtraCharges,
      getListExtraChargeCategory,
      getListExtraCharge,
      addItemExtraCharge,
      removeRow,
      increaseQuantity,
      decreaseQuantity,
      closeModal,
      updateModal,
      totalExtraChargePrice,
      handleExtraChargeCategory,
      handleExtraCharge,
      handleAction,
      resetForm,
      formatIDR,
    };
  },
};
</script>

<style scoped>
@media screen and (max-width: 576px) {
  .modal-title h5 {
    font-size: 1rem;
  }
}
</style>
